import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type {
  CreateAdminApiKey,
  CreateApiEndpoint,
  KeyRetirement,
  UpdateApiEndpoint,
} from '@headline/schemas'
import { api } from './api'
import { fail } from './query-fail'

/**
 * The public API's endpoints (`/api/api-access`, Clipbook admins only) — the overview behind the root
 * plane's API page, and the set-up a workspace's API page offers an admin: create, pause, excluded
 * outlets. Every endpoint arrives with its keys (never a secret or a hash).
 */
export const apiEndpointsQuery = queryOptions({
  queryKey: ['api-endpoints'],
  queryFn: async () => {
    const res = await api.api['api-access'].endpoints.$get()
    if (!res.ok) return fail(res, 'Failed to load API endpoints')
    return res.json()
  },
})

/** Clipbook's API page: every workspace with an endpoint or the product, and endpoints in none. */
export const apiOverviewQuery = queryOptions({
  queryKey: ['api-endpoints', 'overview'],
  queryFn: async () => {
    const res = await api.api['api-access'].overview.$get()
    if (!res.ok) return fail(res, 'Failed to load the API overview')
    return res.json()
  },
})

const refresh = (queryClient: QueryClient) =>
  Promise.all([
    // The list, the overview, and the workspace pages that show an endpoint.
    queryClient.invalidateQueries({ queryKey: ['api-endpoints'] }),
    queryClient.invalidateQueries({ queryKey: ['workspace-api'] }),
    // Attaching topics marks them `api`, which the Topics Library rows carry.
    queryClient.invalidateQueries({ queryKey: ['topic-library'] }),
  ])

export function useCreateApiEndpoint(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreateApiEndpoint) => {
      const res = await api.api['api-access'].endpoints.$post({ json: input })
      if (!res.ok) return fail(res, 'Failed to create the endpoint')
      return res.json()
    },
    onSuccess: () => refresh(queryClient),
  })
}

export function useUpdateApiEndpoint(endpointId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (patch: UpdateApiEndpoint) => {
      const res = await api.api['api-access'].endpoints[':endpointId'].$patch({
        param: { endpointId },
        json: patch,
      })
      if (!res.ok) return fail(res, 'Failed to update the endpoint')
      return res.json()
    },
    onSuccess: () => refresh(queryClient),
  })
}

/**
 * An endpoint's usage per UTC day and key over [from, to), as Clipbook sees it: every key, its internal
 * ones flagged (`/api/api-access/endpoints/:endpointId/usage`). Null where clipbook-api is not configured
 * (503), so the admin section says so rather than showing zeros.
 */
export const apiEndpointUsageQuery = (
  endpointId: string,
  { from, to }: { from: string; to: string },
) =>
  queryOptions({
    queryKey: ['api-endpoints', endpointId, 'usage', from, to],
    queryFn: async () => {
      const res = await api.api['api-access'].endpoints[':endpointId'].usage.$get({
        param: { endpointId },
        query: { from, to },
      })
      if (res.status === 503) return null
      if (!res.ok) return fail(res, 'Failed to load usage')
      return res.json()
    },
    staleTime: 60_000,
  })

/** Mints one of Clipbook's internal keys on the endpoint; resolves to the one response with its secret. */
export function useCreateInternalApiKey(endpointId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: Omit<CreateAdminApiKey, 'internal'>) => {
      const res = await api.api['api-access'].endpoints[':endpointId'].keys.$post({
        param: { endpointId },
        json: { ...input, internal: true },
      })
      if (!res.ok) return fail(res, 'Failed to create the key')
      return res.json()
    },
    onSuccess: () => refresh(queryClient),
  })
}

/** Revokes a key from Clipbook's admin routes, now or after a grace period. */
export function useRevokeApiKey(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async ({ keyId, retire }: { keyId: string; retire: KeyRetirement }) => {
      const res = await api.api['api-access'].keys[':keyId'].revoke.$post({
        param: { keyId },
        json: { retire },
      })
      if (!res.ok) return fail(res, 'Failed to revoke the key')
      return res.json()
    },
    onSuccess: () => refresh(queryClient),
  })
}
